// tslint:disable-next-line:max-line-length
import {
  ComponentFactoryResolver,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  ViewContainerRef
} from '@angular/core';
import { TooltipComponent } from './tooltip.component';
import { TooltipDataSharedService } from './tooltip.dataSharedService';
import { isMobileDevice } from 'axis-components/components/utilities';
@Directive({
  // tslint:disable-next-line:directive-selector
  selector: '[axis-tooltip]',
  host: {
    '[attr.tabindex]': '0',
    class: 'axis-tooltip-trigger'
  }
})
export class TooltipDirective implements OnChanges, OnDestroy {
  private ariaId = 'axisTooltipContent';
  private componentInstance: any;
  private unbindDocumentEvent: Function;
  private containerEl = document.querySelector('body');
  private scrollYPosition: any;
  private unbindKeyEvent: Function;
  private isMobile = false;

  @Input() public color = 'default';
  @Input() placement = 'right';
  @Input() closeOnOutsideClick = false;
  @Input() isOpen = false;
  @Input() content: string;
  @Input() kind: string;
  @Input() item: string;
  @Input() items: any;
  @Input() style: string;
  @Input() axisIncludeComponent: any;
  @Input() axisInclude: string;
  @Input() data: any;
  @Input() tooltipTrigger: string;
  @Input() minwidth: string;
  @Input() width: string;
  @Input() isToggleOnClick: boolean;
  @Input() context: any;
  @Output() onClose: EventEmitter<any> = new EventEmitter<any>();
  @Input('data-content') // property added in ng9 version to support existing applications using data-content.
  get dataContent() {
    return this.content;
  }
  set dataContent(value) {
    this.content = value;
  }

  constructor(
    private viewContainerRef: ViewContainerRef,
    private renderer: Renderer2,
    private resolver: ComponentFactoryResolver,
    private axisTooltipDataSharedService: TooltipDataSharedService,
    private hostElement: ElementRef
  ) {
    this.isMobile = isMobileDevice();
    const thisHostElement = this.getHost();
    if (thisHostElement.tagName !== 'BUTTON') {
      this.renderer.setAttribute(thisHostElement, 'role', 'button');
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isOpen']) {
      if (changes['isOpen'].currentValue !== changes['isOpen'].previousValue) {
        if (changes['isOpen'].currentValue) {
          this.createTooltipInstance(event);
          this.addDocumentListener();
        } else {
          if (typeof this.unbindDocumentEvent === 'function') {
            this.unbindDocumentEvent();
          }
          this.deleteTooltipInstance();
        }
      }
    }
  }

  ngOnDestroy() {
    if (typeof this.unbindDocumentEvent === 'function') {
      this.unbindDocumentEvent();
    }
    this.close();
  }

  @HostListener('blur', ['$event'])
  removeTooltipOnBlur(event: any) {
    this.hideTooltip(event);
  }

  @HostListener('click', ['$event'])
  toggleTooltip(event) {
    if (this.isToggleOnClick || this.isMobile) {
      if (!this.isOpen && this.tooltipTrigger !== 'none') {
        this.isOpen = true;
        this.createTooltipInstance(event);
        // this.renderer.setAttribute(event.target, 'aria-describedby', this.ariaId);
      } else if (this.isOpen && this.tooltipTrigger !== 'none') {
        this.isOpen = false;
        this.deleteTooltipInstance();
        // this.renderer.setAttribute(event.target, 'aria-describedby', '');
      }
    }
  }

  @HostListener('focus', ['$event'])
  appendTooltipOnFocus(event: any) {
    if (this.checkTooltipWithTruncate()) {
      if (!event.clientX) {
        const thisHostElement = this.getHost();
        const elementLocation = thisHostElement.getBoundingClientRect();
        if (this.placement === 'top') {
          event.clientX = elementLocation.x + elementLocation.width / 2;
          event.clientY = elementLocation.y;
        } else if (this.placement === 'bottom') {
          event.clientX = elementLocation.x + elementLocation.width / 2;
          event.clientY = elementLocation.y + elementLocation.height;
        } else if (this.placement === 'left') {
          event.clientX = elementLocation.x;
          event.clientY = elementLocation.y + elementLocation.height / 2;
        } else if (this.placement === 'right') {
          event.clientX = elementLocation.x + elementLocation.width;
          event.clientY = elementLocation.y + elementLocation.height / 2;
        }
      }
      this.showTooltip(event);
    }
  }

  @HostListener('keydown.escape', ['$event'])
  removeTooltipOnKeydown(event) {
    this.hideTooltip(event);
  }

  @HostListener('mouseleave', ['$event'])
  removeTooltipOnMouseout(event) {
    if (!this.isMobile) {
      this.hideTooltip(event);
    }
  }

  @HostListener('mouseenter', ['$event'])
  appendTooltipOnMouseover(event: any) {
    if (!this.isMobile && this.checkTooltipWithTruncate()) {
      this.showTooltip(event);
    }
  }

  /**
   * This will check if axis-truncate directive is applied and need to show tooltip or not.
   * @return Boolean - return true for below scenarios:
   * Scenario 1: when axis-truncate is not applied. - 'axis-truncate-generic' class will not present.
   * OR
   * Scenario 2: when axis-truncate is applied. - 'axis-truncate-generic' and 'axis-truncate-should-tooltip' both classes are present.
   * Else return false.
   */
  checkTooltipWithTruncate() {
    const hostElement = this.getHost();
    if (!hostElement.classList.contains('axis-truncate-generic') ||
       hostElement.classList.contains('axis-truncate-should-tooltip')) {
      return true;
    }
    return false;
  }

  getHost() {
    return this.hostElement.hasOwnProperty('nativeElement')
        ? this.hostElement.nativeElement
        : this.hostElement;
  }

  addDocumentListener() {
    this.unbindDocumentEvent = this.renderer.listen(
      document,
      'click',
      (event: Event) => {
        event.stopPropagation();
        if (
          event.target['parentNode']['className'] === 'axis-tooltip-trigger'
        ) {
          const thisHostElement = this.getHost();
          if (!thisHostElement.contains(event.target) && this.isOpen) {
            this.deleteTooltipInstance();
            this.onClose.emit();
          }
        } else if (event.target['classList'].contains('tooltip-container')) {
          if (this.closeOnOutsideClick === true) {
            this.deleteTooltipInstance();
            this.onClose.emit();
          }
        }
      }
    );
  }

  createTooltipInstance(event) {
    const key = this.axisTooltipDataSharedService.generateUniqueKey();
    this.axisTooltipDataSharedService.setData(this, key);
    const factory = this.resolver.resolveComponentFactory(TooltipComponent);
    this.componentInstance = this.viewContainerRef.createComponent(factory);
    this.componentInstance.instance.uniqueKey = key;
    this.componentInstance.instance.event = event;
    const thisHostElement = this.getHost();
    if (thisHostElement.tagName === 'BUTTON') {
      this.renderer.setAttribute(this.componentInstance.location.nativeElement, 'aria-live', 'assertive');
    }
    this.containerEl.appendChild(this.componentInstance.location.nativeElement);
    if (this.tooltipTrigger === 'none' && !this.isToggleOnClick) {
      this.scrollYPosition = window.pageYOffset;
      document.body.style.top = -this.scrollYPosition + 'px';
      document.body.style.position = 'fixed';
      if (document.body.scrollHeight > window.innerHeight) {
        document.body.classList.add('axis-tooltip-open');
      } else {
        document.body.classList.add('axis-tooltip-hidden-scroll');
      }
    }
    if (this.isMobile) {
      if (this.tooltipTrigger !== 'none') {
        this.scrollYPosition = window.pageYOffset;
        document.body.style.top = -this.scrollYPosition + 'px';
        document.body.style.position = 'fixed';
      }
      if (document.body.scrollHeight > window.innerHeight) {
        document.body.classList.add('axis-tooltip-open');
      } else {
        document.body.classList.add('axis-tooltip-hidden-scroll');
      }
    }
  }

  deleteTooltipInstance() {
    if (this.componentInstance) {
      if (this.containerEl.contains(this.componentInstance.location.nativeElement)) {
        this.containerEl.removeChild(this.componentInstance.location.nativeElement);
      }
      this.componentInstance.destroy();
      const thisHostElement = this.getHost();
      if (thisHostElement.getAttribute('tooltiptrigger') === 'none') {
        thisHostElement.focus();
      }
    }
    if (this.tooltipTrigger === 'none' && !this.isToggleOnClick) {
      document.body.classList.remove('axis-tooltip-open');
      document.body.classList.remove('axis-tooltip-hidden-scroll');
      document.body.style.position = '';
      document.body.removeAttribute('style');
      window.scrollTo(0, this.scrollYPosition);
    }
    if (this.isMobile) {
      document.body.classList.remove('axis-tooltip-open');
      document.body.classList.remove('axis-tooltip-hidden-scroll');
      if (this.tooltipTrigger !== 'none') {
        document.body.style.position = '';
        document.body.removeAttribute('style');
        window.scrollTo(0, this.scrollYPosition);
      }
    }
  }

  close() {
    if (this.isOpen) {
      this.isOpen = false;
      this.deleteTooltipInstance();
    }
    this.onClose.emit();
  }

  private hideTooltip(event) {
    if (!this.isToggleOnClick) {
      if (this.isOpen && this.tooltipTrigger !== 'none') {
        this.isOpen = false;
        this.deleteTooltipInstance();
      }
      if (typeof this.unbindKeyEvent === 'function') {
        this.unbindKeyEvent();
      }
      if (event.target.classList.contains('axis-tooltip-trigger')) {
        this.renderer.removeAttribute(event.target, 'aria-describedby');
      }
    }
  }

  private showTooltip(event) {
    if (!this.isToggleOnClick) {
      if (!this.isOpen && this.tooltipTrigger !== 'none') {
        this.isOpen = true;
        this.createTooltipInstance(event);
        if (event.target.classList.contains('axis-tooltip-trigger')
          && event.target.tagName === 'BUTTON') {
          this.unbindKeyEvent = this.renderer.listen(
            document,
            'keypress',
            (e: KeyboardEvent) => {
              const key = e.key.toLocaleLowerCase();
              if (key === 'enter') {
                this.renderer.setAttribute(e.target, 'aria-describedby', this.ariaId); // per spec and ChromeVox
              }
            });
        } else if (event.target.classList.contains('axis-tooltip-trigger')
          && event.target.tagName !== 'BUTTON') {
          this.renderer.setAttribute(event.target, 'aria-describedby', this.ariaId); // per spec
        }
      }
    }
  }
}
